import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const syncTimezone = async (token) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        await axios.post(`${API}/auth/update-timezone`, { timezone: tz }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {
      // non-critical, ignore
    }
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const role = localStorage.getItem("user_role");
      if (!token || !role) {
        setLoading(false);
        return;
      }
      
      let endpoint = "/auth/me";
      if (role === "student") endpoint = "/auth/student/me";
      else if (role === "tutor") endpoint = "/auth/tutor/me";
      
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_role");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Student auth
  const registerStudent = async (data) => {
    const response = await axios.post(`${API}/auth/student/register`, data, { withCredentials: true });
    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("user_role", "student");
      // Auto-detect and store browser timezone
      syncTimezone(response.data.access_token);
    }
    setUser(response.data);
    return response.data;
  };

  const loginStudent = async (email, password) => {
    const response = await axios.post(`${API}/auth/student/login`, { email, password }, { withCredentials: true });
    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("user_role", "student");
      syncTimezone(response.data.access_token);
    }
    setUser(response.data);
    return response.data;
  };

  // Tutor auth
  const loginTutor = async (accessCode) => {
    const response = await axios.post(`${API}/auth/tutor/login`, { access_code: accessCode }, { withCredentials: true });
    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("user_role", "tutor");
      syncTimezone(response.data.access_token);
    }
    setUser(response.data);
    return response.data;
  };

  const setupTutor = async (data) => {
    const token = localStorage.getItem("access_token");
    await axios.post(`${API}/auth/tutor/setup`, data, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    await checkAuth();
  };

  // Admin auth
  const loginAdmin = async (email, password) => {
    const response = await axios.post(`${API}/auth/admin/login`, { email, password }, { withCredentials: true });
    if (response.data.access_token) {
      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("user_role", "admin");
      syncTimezone(response.data.access_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_role");
      setUser(null);
    }
  };

  const value = {
    user,
    setUser,
    loading,
    registerStudent,
    loginStudent,
    loginTutor,
    setupTutor,
    loginAdmin,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
